import { Button } from './ui/button';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { 
  UserCheck,
  UserX,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { useState } from 'react';

interface TeamMember {
  id: number;
  firstname: string;
  lastname: string;
  emailid: string;
  status: 'Active' | 'Inactive' | 'Pending';
}

interface ActivateDeactivateTeamMemberProps {
  isOpen: boolean;
  onClose: () => void;
  member: TeamMember | null;
  onSuccess?: () => void;
  activateMember: (memberId: number) => Promise<any>;
  deactivateMember: (memberId: number) => Promise<any>;
}

export function ActivateDeactivateTeamMember({ 
  isOpen, 
  onClose, 
  member, 
  onSuccess,
  activateMember,
  deactivateMember
}: ActivateDeactivateTeamMemberProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isActivating = member?.status !== 'Active';
  const actionText = isActivating ? 'activate' : 'deactivate';
  const ActionIcon = isActivating ? UserCheck : UserX;

  const handleAction = async () => {
    if (!member) return;

    setIsProcessing(true);
    setError(null);
    
    try {
      const response = isActivating 
        ? await activateMember(member.id)
        : await deactivateMember(member.id);
      
      if (response.data) {
        setIsProcessing(false);
        setIsComplete(true);
        
        if (onSuccess) {
          onSuccess();
        }
        
        setTimeout(() => {
          onClose();
          setIsComplete(false);
        }, 1500);
      } else {
        throw new Error(response.message || `Failed to ${actionText} member`);
      }
    } catch (error) {
      setIsProcessing(false);
      setError(error instanceof Error ? error.message : `Failed to ${actionText} member. Please try again.`);
    }
  };

  const handleClose = () => {
    setError(null);
    setIsComplete(false);
    onClose();
  };

  const renderContent = () => {
    if (isComplete) {
      return (
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Member {isActivating ? 'Activated' : 'Deactivated'} Successfully!
          </h3>
          <p className="text-muted-foreground">
            {member?.firstname} {member?.lastname} has been {isActivating ? 'activated' : 'deactivated'} 
            {isActivating ? ' and can now access the system.' : ' and no longer has system access.'}
          </p>
        </div>
      );
    }

    if (isProcessing) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {isActivating ? 'Activating' : 'Deactivating'} Member...
          </h3>
          <p className="text-muted-foreground">
            {isActivating ? 'Activating' : 'Deactivating'} {member?.firstname} {member?.lastname}...
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <AlertTriangle className={`w-8 h-8 ${isActivating ? 'text-green-500' : 'text-amber-500'}`} />
          </div>
          <div className="flex-1">
          
            <p className="text-muted-foreground">
              Are you sure you want to {actionText} {member?.firstname} {member?.lastname}?
            </p>
            
        
        
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center space-x-2">
            <ActionIcon className={`w-5 h-5 ${isActivating ? 'text-green-600' : 'text-amber-600'}`} />
            <span>{isActivating ? 'Activate' : 'Deactivate'} Team Member</span>
          </DialogTitle>
          <DialogDescription>
            {isActivating 
              ? 'This action will activate the team member and restore their system access.'
              : 'This action will deactivate the team member and remove their system access.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {renderContent()}
        </div>
        
        {!isProcessing && !isComplete && (
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              className={`${isActivating 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-amber-600 hover:bg-amber-700'
              } text-white`}
            >
              {isActivating ? 'Activate' : 'Deactivate'} Member
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
